import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import { Typography } from 'components/uiKit/Typography'
import {
  BrandButtonFont,
  BrandFormFontBase,
  BrandGoBack,
  BrandRichTextFontForms,
  BrandRichTextTypography,
} from 'services/Branding/components'
import { FONT_FIELDS } from 'services/Branding/fieldConfigs'
import { genSchema } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { SimulatorFormBrandType } from './simulatorTypes'

const FONT_FORM_SCHEMA = genSchema(SCHEMA.simulator.font, {
  ...FONT_FIELDS,
  navigation: FONT_FIELDS.base,
  placeholder: FONT_FIELDS.base,
})

const TypographyNavigation: SimulatorFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.navigation}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('input.label.numbering')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontBase>
  )
}

const TypographyPlaceholder: SimulatorFormBrandType = ({ setForm, device, subConfigs }) => {
  const fontPath = useMemo(() => ['font', device], [device])
  const fontSchema = useUpdateSchema(fontPath, subConfigs, FONT_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <BrandFormFontBase font={fontSchema.placeholder}>
      <BrandGoBack onClick={handleSetTypography}>
        <Typography weight='medium'>{t('input.label.placeholder')}</Typography>
      </BrandGoBack>
      <Typography styleType='hint'>{t('page.brand.fonts.inheritHint')}</Typography>
    </BrandFormFontBase>
  )
}

const SimulatorTypography: SimulatorFormBrandType = ({ element, edited, device, setForm }) => {
  const { font } = element || {}
  const values = useElementFontValues('simulator', device, font)
  const styles = useElementFontCss('simulator', device, font)

  const goBack = useCallback(() => setForm?.(BrandFormEnum.STYLE), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottomSpace gap sides>
        <BrandGoBack onClick={goBack}>
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
        </BrandGoBack>
        <BrandButtonFont
          edited={edited.font?.navigation}
          font='navigation'
          onClick={setForm}
          styles={styles.navigation}
          values={values.navigation}
        >
          {t('elements.list.tags.numbering')}
        </BrandButtonFont>
        <BrandButtonFont
          edited={edited.font?.placeholder}
          font='placeholder'
          onClick={setForm}
          styles={styles.placeholder}
          values={values.placeholder}
        >
          {t('input.label.placeholder')}
        </BrandButtonFont>
        <BrandRichTextTypography
          edited={edited.font}
          setActiveFont={setForm}
          styles={styles}
          values={values}
        />
      </Padding>
    </LayoutScroll>
  )
}

const FONTS_FORM_MAP = {
  [BrandFormEnum.TYPOGRAPHY]: SimulatorTypography,
  navigation: TypographyNavigation,
  placeholder: TypographyPlaceholder,
  ...BrandRichTextFontForms,
}

export default FONTS_FORM_MAP
